/*
 * Token builder search across categories.
 *
 * The builder search looks only in the open category. This script also lists
 * matching tokens from the other categories in the section setting
 * "Categories to search". Each match is a real .tokenVariant row, cloned from
 * a row on the page, so the theme's + / - buttons, "Personalize the Back?"
 * and the add-to-cart code work without changes.
 *
 * Rules:
 * - Never remove a selected cross row: add-to-cart reads every
 *   .tokenVariant.selected on the page.
 * - Only categories with the same price and the same discount tag as the
 *   page are searched, so the price and tier bar on the page stay true.
 * - The "We don't have that token" message shows only when no category
 *   has a match.
 *
 * Works with the theme search (assets/product.js, keyup) and with the
 * Intelligems builder search (filterPanel, input). This script runs after both.
 */
(function () {
  'use strict';

  var config = document.querySelector('[data-token-cross-search]');
  if (!config || window.tokenCrossSearch) return;

  var CROSS_CLASS = 'tokenVariant--cross';
  var MAX_MATCHES = 40;

  var pagePrice = parseInt(config.getAttribute('data-price'), 10);
  var pageDiscount = config.getAttribute('data-discount') === 'true';
  var rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  var categories = (config.getAttribute('data-categories') || '')
    .split('\n')
    .map(function (line) {
      var parts = line.split('|');
      return { handle: (parts[0] || '').trim(), label: (parts[1] || '').trim() };
    })
    .filter(function (category) {
      return category.handle;
    });
  if (!categories.length) return;

  var index = null;
  var loading = null;

  function handleFromPath(path) {
    var match = String(path || '').match(/\/products\/([^/?#]+)/);
    return match ? match[1] : '';
  }

  function loadIndex() {
    if (loading) return loading;
    loading = Promise.all(
      categories.map(function (category) {
        return fetch(rootUrl + 'products/' + category.handle + '?view=token-index', {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' }
        })
          .then(function (response) {
            if (!response.ok) throw new Error('token index ' + response.status);
            return response.text();
          })
          .then(function (text) {
            var data = JSON.parse(text.slice(text.indexOf('{'), text.lastIndexOf('}') + 1));
            return {
              handle: category.handle,
              label: category.label,
              discount: data.discount === true,
              tokens: data.tokens || []
            };
          })
          .catch(function () {
            return null;
          });
      })
    ).then(function (results) {
      index = results.filter(Boolean);
      return index;
    });
    return loading;
  }

  // The panel that holds a search input: an Intelligems category panel, or the theme list.
  function contextFor(input) {
    var panel = input.closest('[data-exp-cat-panel]') || input.closest('.tokens--container-vertical');
    if (!panel) return null;
    var list = panel.querySelector('.tokensList');
    if (!list) return null;
    var key = panel.getAttribute('data-exp-cat-panel');
    return { panel: panel, list: list, handle: handleFromPath(key || window.location.pathname) };
  }

  function localRows(list) {
    return Array.prototype.slice.call(list.querySelectorAll('.tokenVariant:not(.' + CROSS_CLASS + ')'));
  }

  function crossRows(list) {
    return Array.prototype.slice.call(list.querySelectorAll('.' + CROSS_CLASS));
  }

  function isShown(row) {
    return row.style.display !== 'none' && !row.hidden;
  }

  function findMatches(query, context) {
    var onPage = {};
    localRows(context.list).forEach(function (row) {
      onPage[row.getAttribute('data-variant-id')] = true;
    });
    var matches = [];
    (index || []).forEach(function (category) {
      if (category.handle === context.handle || category.discount !== pageDiscount) return;
      category.tokens.forEach(function (token) {
        if (matches.length >= MAX_MATCHES || onPage[token.id] || token.p !== pagePrice) return;
        var title = String(token.t).toLowerCase();
        var alt = String(token.a || '').toLowerCase();
        if (title.indexOf(query) >= 0 || (alt && alt.indexOf(query) >= 0)) {
          matches.push({ token: token, category: category });
        }
      });
    });
    return matches;
  }

  // A clean, unselected row to clone. Prefer a non-dated row, as the theme does.
  function templateRow(context) {
    var rows = localRows(context.list).filter(function (row) {
      return !row.classList.contains('selected') && !row.classList.contains('datedToken');
    });
    return rows[0] || null;
  }

  function buildRow(template, match) {
    var token = match.token;
    var row = template.cloneNode(true);
    row.classList.remove('selected', 'highlighted', 'token--sold-out');
    row.classList.add(CROSS_CLASS);
    row.removeAttribute('style');
    row.removeAttribute('data-team');
    row.setAttribute('data-variant-id', token.id);
    row.setAttribute('data-cross-handle', match.category.handle);
    if (token.f) row.setAttribute('data-feature-img', token.f);
    else row.removeAttribute('data-feature-img');

    row.querySelectorAll(
      '.personalizeBack, .tokens--variant__personalization, .swym-notify-container, .tokens--variant__tag, .tokens--variant__title-alt'
    ).forEach(function (node) {
      node.remove();
    });

    var image = row.querySelector('.tokens--variant__image');
    if (image) {
      image.innerHTML = '';
      if (token.i) {
        var img = document.createElement('img');
        img.src = token.i;
        img.width = 200;
        img.height = 200;
        img.loading = 'lazy';
        img.alt = token.t + ' Image';
        image.appendChild(img);
      }
    }

    var title = row.querySelector('.tokenTitle');
    if (title) {
      title.textContent = token.t;
      title.setAttribute('data-alt-title', token.a || '');
    }
    var text = row.querySelector('.tokens--variant__text');
    if (text) {
      if (token.a) {
        var alt = document.createElement('span');
        alt.className = 'tokens--variant__title-alt';
        alt.textContent = '(' + token.a + ')';
        text.appendChild(alt);
      }
      var label = document.createElement('span');
      label.className = 'tokenCrossCategory';
      label.textContent = match.category.label;
      text.appendChild(label);
    }

    row.querySelectorAll('[data-variant-id]').forEach(function (node) {
      node.setAttribute('data-variant-id', token.id);
    });
    var quantity = row.querySelector('.quantity');
    if (quantity) {
      quantity.value = '0';
      quantity.removeAttribute('style');
    }
    var minus = row.querySelector('.button-minus');
    if (minus) {
      minus.classList.add('disabled');
      minus.removeAttribute('style');
    }
    var plus = row.querySelector('.button-plus');
    if (plus) plus.classList.remove('disabled');
    return row;
  }

  // visible = at least one token shows. Hide the "not found" messages then.
  // Without results, put back only a theme message this script hid, and show
  // the message once: the theme's copy (the one with the Custom Tokens link)
  // wins, and the builder's own copy shows only when the theme's is not on
  // screen. Both used to show, one under the other (merchant, 2026-10-16).
  function setNotFound(context, visible) {
    var themeShown = false;
    document.querySelectorAll('.Product__Info .NotFound').forEach(function (node) {
      if (visible && node.style.display !== 'none') {
        node.setAttribute('data-cross-hidden', node.style.display);
        node.style.display = 'none';
      } else if (!visible && node.hasAttribute('data-cross-hidden')) {
        node.style.display = node.getAttribute('data-cross-hidden');
        node.removeAttribute('data-cross-hidden');
      }
      if (!visible && node.getClientRects().length) themeShown = true;
    });
    var builderNotFound = context.panel.querySelector('.exp-2step-builder__notfound');
    if (builderNotFound) builderNotFound.hidden = visible || themeShown;
    if (visible) {
      context.list.style.height = '';
      var shadow = context.panel.querySelector('.container-shadow');
      if (shadow) shadow.style.opacity = '1';
    }
  }

  function track(action, label) {
    if (typeof window.ga === 'function') {
      try {
        window.ga('send', 'event', 'Tokens', action, label);
      } catch (error) {}
    }
  }

  function render(input) {
    var context = contextFor(input);
    if (!context) return;
    var query = input.value.trim().toLowerCase();
    var list = context.list;

    // Unselected cross rows go; selected ones stay (they are in the order).
    crossRows(list).forEach(function (row) {
      if (row.classList.contains('selected')) row.style.display = '';
      else row.remove();
    });
    list.classList.toggle('tokensList--searching', query !== '');
    if (!query) return;

    if (!index) {
      // Hide "We don't have that token" while the other categories load.
      var localVisible = localRows(list).some(isShown);
      if (!localVisible) setNotFound(context, true);
      list.classList.add('tokensList--loading');
      loadIndex().then(function () {
        list.classList.remove('tokensList--loading');
        if (input.value.trim().toLowerCase() === query) render(input);
      });
      return;
    }

    var template = templateRow(context);
    var matches = template ? findMatches(query, context) : [];
    var selectedById = {};
    crossRows(list).forEach(function (row) {
      selectedById[row.getAttribute('data-variant-id')] = row;
    });

    var shownLabels = {};
    matches.forEach(function (match) {
      var existing = selectedById[match.token.id];
      if (existing) {
        list.appendChild(existing);
      } else {
        list.appendChild(buildRow(template, match));
      }
      shownLabels[match.category.label] = true;
    });

    // A selected cross row that does not match the query hides like a local row.
    crossRows(list).forEach(function (row) {
      var titleNode = row.querySelector('.tokenTitle');
      var title = (titleNode ? titleNode.textContent : '').toLowerCase();
      var alt = String((titleNode && titleNode.getAttribute('data-alt-title')) || '').toLowerCase();
      row.style.display = title.indexOf(query) >= 0 || (alt && alt.indexOf(query) >= 0) ? '' : 'none';
    });

    var anyVisible = localRows(list).some(isShown) || crossRows(list).some(isShown);
    setNotFound(context, anyVisible);
    if (matches.length) track('search-cross-result-shown', Object.keys(shownLabels).join(','));
  }

  var pending = null;
  function schedule(event) {
    var input = event.target;
    if (!input || !input.matches || !input.matches('.custom-search2')) return;
    if (pending) clearTimeout(pending);
    // Run after the theme (keyup) and Intelligems (input) handlers.
    pending = setTimeout(function () {
      pending = null;
      render(input);
    }, 0);
  }

  document.addEventListener('input', schedule);
  document.addEventListener('keyup', schedule);
  document.addEventListener('focusin', function (event) {
    if (event.target && event.target.matches && event.target.matches('.custom-search2')) loadIndex();
  });
  document.addEventListener('click', function (event) {
    var close = event.target.closest && event.target.closest('.custom-search-container .Close__Icon');
    if (close) {
      var input = close.parentElement.querySelector('.custom-search2');
      if (input) setTimeout(function () { render(input); }, 0);
      return;
    }
    var plus = event.target.closest && event.target.closest('.' + CROSS_CLASS + ' .button-plus');
    if (plus) {
      var row = plus.closest('.' + CROSS_CLASS);
      track('search-cross-add', row.getAttribute('data-cross-handle'));
    }
  });

  window.tokenCrossSearch = { load: loadIndex };
})();
